/**
 * « Qui voit quoi ? » (page « Mon réseau » de l'élève) : ce que chaque type de
 * lien donne à voir, en une phrase, + le SEUL réglage qui dépend de l'élève :
 * montrer sa progression SKILLCARDS à ses camarades (migration 0285, même
 * réglage que « Mon profil → Ce que je reçois »). Repliable : c'est de
 * l'information de référence, pas une tâche.
 *
 * Sources : RLS des évals (0322 : prof lié, jamais les parents), diagnostics
 * (0325 : parent lié), « Mon cercle » (0285 : camarades selon le réglage),
 * profs suivis (unilatéral : l'élève voit leurs publications).
 */

import { ChevronDown, Eye } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { usePeerVisibility, useSetPeerVisibility } from '@/lib/notification-preferences';

export function WhoSeesWhat({ studentId }: { studentId: string }) {
  const visible = usePeerVisibility(studentId);
  const set = useSetPeerVisibility(studentId);
  const on = !!visible.data;
  return (
    <details className="border-ink-100 ocean:bg-white/5 group rounded-2xl border bg-white">
      <summary className="text-ink-900 flex min-h-[2.75rem] cursor-pointer list-none items-center gap-2 px-4 py-2 text-sm font-semibold [&::-webkit-details-marker]:hidden">
        <Eye className="text-primary-500 h-4 w-4 shrink-0" aria-hidden />
        <span className="flex-1">Qui voit quoi ?</span>
        <ChevronDown
          className="text-ink-400 h-4 w-4 shrink-0 transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className="space-y-3 px-4 pb-4">
        <dl className="text-ink-700 space-y-2 text-sm">
          <div>
            <dt className="text-ink-900 font-semibold">Ton prof</dt>
            <dd>
              Voit tes exercices, ta progression, tes SKILLCARDS et les notes que tu saisis. Il peut
              te donner des devoirs et des cartes à travailler.
            </dd>
          </div>
          <div>
            <dt className="text-ink-900 font-semibold">Tes parents</dt>
            <dd>Suivent ta progression et tes diagnostics. Ils ne voient pas tes notes.</dd>
          </div>
          <div>
            <dt className="text-ink-900 font-semibold">Tes camarades</dt>
            <dd>
              Peuvent te défier sur une SKILLCARD. Ils voient ta maîtrise dans « Mon cercle »
              seulement si tu l’autorises :
            </dd>
          </div>
        </dl>
        <div className="border-ink-100 flex items-center gap-2 rounded-xl border p-3">
          <p className="text-ink-900 min-w-0 flex-1 text-sm font-medium">
            Montrer ma progression à mes camarades
          </p>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label="Montrer ma progression à mes camarades"
            disabled={visible.isLoading || set.isPending}
            onClick={() => set.mutate(!on)}
            className="flex h-11 w-12 shrink-0 items-center justify-center disabled:opacity-50"
          >
            <span
              className={cn(
                'relative inline-flex h-6 w-11 rounded-full border-2 border-transparent transition-colors',
                on ? 'bg-accent-400' : 'bg-ink-200',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform',
                  on ? 'translate-x-5' : 'translate-x-0',
                )}
              />
            </span>
          </button>
        </div>
        <p className="text-ink-500 text-xs">
          Les profs que tu suis ne voient rien de toi : tu vois seulement leurs publications dans
          ton Feed. Retirer un contact coupe le lien tout de suite.
        </p>
      </div>
    </details>
  );
}
